import { Globals } from "../globals";

/**
 * Helpers for building Firestore paths under a single Gemeinde (tenant).
 *
 * All per-Gemeinde data lives in subcollections of `gemeinden/{gemeindeId}`.
 * These helpers return path-segment arrays that can be spread straight into the
 * modular Firestore SDK, e.g.:
 *
 *   collection(db, ...gemeindeSubPath(gemeindeId, Globals.PREACHER_SUBCOLLECTION))
 *   doc(db, ...gemeindeSubPath(gemeindeId, Globals.PREACHER_SUBCOLLECTION, key))
 */

/** Path segments for the Gemeinde document itself: gemeinden/{gemeindeId}. */
export function gemeindeDocPath(gemeindeId: string): [string, string] {
    assertGemeindeId(gemeindeId);
    return [Globals.GEMEINDEN_COLLECTION, gemeindeId];
}

/**
 * Path segments for a subcollection (and optional nested segments) of a Gemeinde:
 * gemeinden/{gemeindeId}/{...sub}.
 *
 * Returns a tuple (at least two leading segments) so it can be spread straight
 * into collection(db, ...) / doc(db, ...), whose overloads require a guaranteed
 * first path segment after the Firestore instance.
 */
export function gemeindeSubPath(gemeindeId: string, ...sub: string[]): [string, string, ...string[]] {
    assertGemeindeId(gemeindeId);
    return [Globals.GEMEINDEN_COLLECTION, gemeindeId, ...sub];
}

/**
 * Throws if no active Gemeinde is set. Calling a data function without a
 * gemeindeId is a programming error (the caller must wait for the Gemeinde
 * context to resolve), so we fail loudly rather than reading the wrong path.
 */
function assertGemeindeId(gemeindeId: string): void {
    if (!gemeindeId || typeof gemeindeId !== "string") {
        throw new Error("No active gemeindeId – cannot build a Firestore path.");
    }
}
